/**
 * Criteria Data Transformer
 *
 * Single source of truth for transforming data between Redux (API) format
 * and component (UI) format. Uses TYPE_REGISTRY for all type metadata.
 */

import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import {
  TYPE_REGISTRY,
  getEffectiveType,
  isStaticType,
  isSingletonType,
} from '../typeRegistry';

/**
 * Component data format used internally by UI components.
 * Uses standardized field names for consistency.
 */
export interface ComponentData {
  id: string;
  type: CriteriaComponentType;
  connector: 'ANY' | 'ALL';
  text: string;
  // Data fields - only one will be populated based on type
  codes?: any[];
  providers?: any[];
  specialties?: any[];
  payerTypes?: any[];
  payerNames?: any[];
  encounters?: any[];
  codeSets?: any[];
  crmStatus?: any[];
  entityAttribute?: any[];
  entityType?: any[];
  filterSets?: any[];
  openPaymentsCompany?: any[];
  openPaymentsCategory?: any[];
  providerLists?: any[];
  educationalInstitutions?: any[];
  educationFocus?: any[];
  educationProgramType?: any[];
}

/**
 * Redux input format as stored in criteria_inputs.
 * Uses API field names and may include ui_metadata.
 */
export interface ReduxInput {
  type: CriteriaComponentType;
  connector?: 'ANY' | 'ALL';
  ui_key?: string;
  ui_metadata?: any[];
  [key: string]: any; // Dynamic data fields based on type
}

/**
 * Mapping from criteria type to component field name.
 * Since all redux fields are now 'value', we map by type instead.
 */
const TYPE_TO_COMPONENT_FIELD: Record<CriteriaComponentType, string> = {
  codes: 'codes',
  education_focus: 'educationFocus',
  education_program_type: 'educationProgramType',
  educational_institution: 'educationalInstitutions',
  providers: 'providers',
  specialties: 'specialties',
  payer_type: 'payerTypes',
  payer_name: 'payerNames',
  provider_cohort: 'encounters',
  provider_list: 'providerLists',
  code_set: 'codeSets',
  crm_status: 'crmStatus',
  entity_attribute: 'entityAttribute',
  entity_type: 'entityType',
  filter_set: 'filterSets',
  open_payments: 'openPaymentsCompany',
  open_payments_category: 'openPaymentsCategory',
  this_provider: 'thisProvider',
  this_site: 'thisSite',
};

/**
 * Get the component field name for a given type.
 */
function getComponentFieldForType(type: CriteriaComponentType): string {
  return TYPE_TO_COMPONENT_FIELD[type] || type;
}

/**
 * Transform Redux input format to component data format.
 *
 * Handles:
 * - Field name mapping (payer_type -> payerTypes)
 * - Singleton types (cohort_id -> [cohort_id])
 * - ui_key restoration (this_provider with ui_key='this_site' -> this_site)
 *
 * @param componentId - The component's unique identifier
 * @param input - Redux input data from criteria_inputs
 * @returns ComponentData in UI format
 */
export function fromReduxInput(
  componentId: string,
  input: ReduxInput
): ComponentData {
  const effectiveType = getEffectiveType(input.type, input.ui_key);
  const metadata = TYPE_REGISTRY[effectiveType];
  const componentField = getComponentFieldForType(effectiveType);

  // Start with empty component data
  const componentData: ComponentData = {
    id: componentId,
    type: effectiveType,
    connector: (input.connector as 'ANY' | 'ALL') || metadata.defaultConnector,
    text: '',
    codes: undefined,
    providers: undefined,
    specialties: undefined,
    payerTypes: undefined,
    payerNames: undefined,
    encounters: undefined,
    codeSets: undefined,
    crmStatus: undefined,
    entityType: undefined,
    filterSets: undefined,
    openPaymentsCompany: undefined,
    openPaymentsCategory: undefined,
    providerLists: undefined,
  };

  // Handle static types (no data)
  if (isStaticType(effectiveType)) {
    return componentData;
  }

  // Get data from Redux input — all types now use 'value'
  let data: any[] = [];

  if (isSingletonType(effectiveType)) {
    // Singleton: wrap single value in array
    const value = input.value;
    if (value) {
      data = [value];
    }
  } else {
    // Array: use as-is
    data = input.value || [];
  }

  // Set the appropriate component field
  (componentData as any)[componentField] = data;

  return componentData;
}

/**
 * Transform component data format to Redux input format.
 *
 * Handles:
 * - Field name mapping (payerTypes -> payer_type)
 * - Singleton types ([cohort] -> cohort.id)
 * - ID extraction using metadata.getItemIds
 *
 * @param type - The component type
 * @param data - Data array (full objects with ui_metadata)
 * @param connector - The connector value
 * @returns Redux input format for criteria_inputs
 */
export function toReduxInput(
  type: CriteriaComponentType,
  data: any[],
  connector: 'ANY' | 'ALL'
): ReduxInput {
  const metadata = TYPE_REGISTRY[type];

  // Handle static types
  if (isStaticType(type)) {
    return {
      type,
      connector,
    };
  }

  // Extract IDs from full objects using registry's getItemIds
  const ids = metadata.getItemIds(data);

  return {
    type,
    [metadata.reduxDataField]: ids,
    ui_metadata: data,
    connector,
  };
}

/**
 * Transform a Map of Redux inputs to a Map of ComponentData.
 * Used by the main component to transform criteria_inputs.
 *
 * @param criteriaInputs - Object of componentId -> ReduxInput
 * @returns Map of componentId -> ComponentData
 */
export function transformCriteriaInputsToComponents(
  criteriaInputs: Record<string, ReduxInput> | undefined
): Map<string, ComponentData> {
  const map = new Map<string, ComponentData>();

  if (!criteriaInputs) {
    return map;
  }

  Object.entries(criteriaInputs).forEach(([id, input]) => {
    map.set(id, fromReduxInput(id, input));
  });

  return map;
}

/**
 * Check if a Redux input has any data.
 * Used to determine if a component should be shown as empty.
 */
export function hasData(input: ReduxInput): boolean {
  if (isStaticType(input.type)) {
    return true; // Static components always "have data"
  }

  const data = input.value;

  if (isSingletonType(input.type)) {
    return !!data;
  }

  return Array.isArray(data) && data.length > 0;
}

/**
 * Merge new items into existing Redux input data.
 * Used by slash command to add items to existing components.
 *
 * @param existingInput - Current Redux input
 * @param newItem - New item to add (full object)
 * @returns Updated Redux input with merged data
 */
export function mergeItemIntoInput(
  existingInput: ReduxInput,
  newItem: any
): ReduxInput {
  const type = existingInput.type;
  const metadata = TYPE_REGISTRY[type];

  // Can't merge into static or singleton types
  if (isStaticType(type) || isSingletonType(type)) {
    return existingInput;
  }

  const currentItems = existingInput.ui_metadata || [];

  // Check for duplicates using metadata's getItemId
  const itemId = metadata.getItemId(newItem);
  const alreadyExists = currentItems.some(
    (item: any) => metadata.getItemId(item) === itemId
  );

  if (alreadyExists) {
    return existingInput;
  }

  const mergedItems = [...currentItems, newItem];
  const ids = metadata.getItemIds(mergedItems);

  return {
    ...existingInput,
    [metadata.reduxDataField]: ids,
    ui_metadata: mergedItems,
  };
}
